import { plural, t } from "@lingui/core/macro";
import { CruiseItineraryFragment, HotelFragment } from "types/graphql-types";
import { ECommerceStatus } from "../ssc-api/useECommerceStatus";
import { ButtonProps } from "@silversea-ssc/ui-components-react";
import { AvailableWebPrice } from "@silversea-ssc/dotcom-api-types/Prices";
import { NON_BREKING_SPACE } from "./strings";
import { formatCruisesPlural } from "./common-plurals";
import { P, match } from "ts-pattern";
import { isPriceWaitlist } from "components/Prices/utils";

export const isRealPort = (portCode: string | undefined | null): boolean => {
  if (!portCode) return false;
  return !portCode.startsWith("ZZ");
};

export const getGeoAreaStats = (
  itinerary: (CruiseItineraryFragment | undefined)[]
): { portsCount: number; countriesCount: number } => {
  const stats = getItineraryItemsStats(itinerary);

  return {
    portsCount: stats.ports.size,
    countriesCount: stats.countries.size
  };
};

export const getItineraryItemsStats = (
  itinerary: (CruiseItineraryFragment | undefined)[]
) => {
  return itinerary.reduce(
    (acc, i) => {
      const port = i?.port;
      if (!port || !isRealPort(port.data?.code))
        return { ...acc, daysAtSeaCount: acc.daysAtSeaCount + 1 };

      const { ports, countries } = acc;
      const country = port.region?.country || port.data?.country;

      ports.add(port?.data?.name?.localized);
      countries.add(country?.name?.localized);
      const shorexCount = i.confirmedShorexesCount || 0;

      return {
        ...acc,
        ports,
        countries,
        shorexCount: acc.shorexCount + shorexCount
      };
    },
    {
      ports: new Set(),
      countries: new Set(),
      daysAtSeaCount: 0,
      shorexCount: 0
    }
  );
};

export const buildCruiseName = (
  departurePortName: string | undefined,
  arrivalPortName: string | undefined
): string => {
  if (!departurePortName || !arrivalPortName) return "";

  return `${departurePortName} ${t`port-to-port`} ${arrivalPortName}`;
};

export const buildNonBreakingCruiseName = (
  departurePortName: string | undefined,
  arrivalPortName: string | undefined
): string => {
  if (!departurePortName || !arrivalPortName) return "";

  return `${nonBreakingSpaceCruiseName(
    departurePortName
  )} ${t`port-to-port`} ${nonBreakingSpaceCruiseName(arrivalPortName)}`;
};

export const nonBreakingSpaceCruiseName = (name: string) =>
  name.indexOf(",") > -1
    ? name.replace(/\s+(?!(?<=,\s))/g, NON_BREKING_SPACE)
    : name.replace(/ (?!([^ ]*)$)/g, NON_BREKING_SPACE);

// export const specialPackageIsVisible =
//   (
//     geoLocation: Geolocation,
//     cruiseCode: string,
//     departureDate: string | undefined
//   ) =>
//   (
//     specialPackage: CruiseSpecialPackageFragment | undefined
//   ): specialPackage is CruiseSpecialPackageFragment => {
//     if (!specialPackage || !departureDate) return false;

//     if (
//       !matchesAny(
//         specialPackage?.locationFilters || [],
//         geoLocation.countryCode
//       )
//     )
//       return false;
//     if (specialPackage?.includedCruiseCodes?.includes(cruiseCode)) return true;
//     if (specialPackage?.excludedCruiseCodes?.includes(cruiseCode)) return false;
//     if (
//       specialPackage?.years &&
//       specialPackage.years
//         .filter(isPresent)
//         .some((year) => departureDate.startsWith(year))
//     )
//       return true;
//     return false;
//   };

export const buttonVariantForRequestAQuote = (
  ecommerceStatus: ECommerceStatus | undefined,
  isCruiseWaitlist: boolean,
  isSuiteWaitlist: boolean
): ButtonProps["variant"] =>
  match([ecommerceStatus, isCruiseWaitlist, isSuiteWaitlist])
    .with([P.any, true, P.boolean], () => "primary" as const)
    .with([P.any, false, false], () => "primary" as const)
    .otherwise(() => "secondary" as const);

export const cruiseIsWaitlist = (
  price: { priceFrom?: AvailableWebPrice } | undefined
): boolean => isPriceWaitlist(price?.priceFrom);

export const cruisesLabel = (cruises: number) => formatCruisesPlural(cruises);

export const cruisesLabelOcean = (cruises: number) =>
  plural(cruises, {
    one: "# Ocean Cruise",
    other: "# Ocean Cruises",
    zero: ""
  });

export const cruisesLabelExpedition = (cruises: number) =>
  plural(cruises, {
    one: "# Expedition Cruise",
    other: "# Expedition Cruises",
    zero: ""
  });

export const cruiseTypeTransformer = (
  cruiseType: "Classic" | "Expedition" | undefined
) => {
  return cruiseType === "Expedition" ? "expedition" : "ocean";
};

export const includesSilverseaHotel = (
  preHotels: HotelFragment[],
  postHotels: HotelFragment[]
) =>
  preHotels.some((h) => h.code == "PWI003") ||
  postHotels.some((h) => h.code == "PWI003");
